import { ref, onUnmounted } from 'vue';
import { useToast } from '@/composables/common/useToast';

/**
 * useCopyToClipboard — copia texto al portapapeles con feedback.
 *
 * Centraliza el acceso a `navigator.clipboard` (que hoy se repite inline en
 * ~7 componentes) y resuelve dos cosas que ese uso inline no cubre:
 *
 *  - **Fallback**: si el navegador no expone la Clipboard API (contexto no
 *    seguro, WebView viejo), cae a un `<textarea>` temporal + `execCommand`,
 *    que es el patrón clásico y sigue funcionando donde la API no está.
 *  - **Feedback**: expone `copied` durante `feedbackMs` para que el componente
 *    pinte el estado "copiado" (icono, tooltip) sin gestionar el temporizador,
 *    y opcionalmente muestra un toast con `successMessage`.
 *
 * No habla con el store: devuelve estado y, si se le pide, notifica.
 *
 * @param {{ feedbackMs?: number, successMessage?: string|null, errorMessage?: string|null }} [opciones]
 * @returns {{
 *   copied: import('vue').Ref<boolean>,
 *   copy: (text: string|number|null|undefined) => Promise<boolean>
 * }}
 */
export function useCopyToClipboard({ feedbackMs = 1500, successMessage = null, errorMessage = null } = {}) {
    const toast = useToast();
    const copied = ref(false);
    let timer = null;

    function limpiarTimer() {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
    }

    /**
     * Copia `text`. Devuelve true si el portapapeles quedó con el valor.
     * @param {string|number|null|undefined} text
     * @returns {Promise<boolean>}
     */
    async function copy(text) {
        const value = text == null ? '' : String(text);
        if (!value) return false;

        try {
            if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(value);
            } else if (copiarConTextarea(value) === false) {
                // `execCommand` devuelve false cuando el navegador rechaza la
                // copia sin lanzar excepción (permisos, contexto no seguro): hay
                // que comunicarlo. Darla por buena mostraría "copiado" con el
                // portapapeles intacto, que es peor que no avisar.
                return fallo();
            }

            copied.value = true;
            if (successMessage) toast.success(successMessage);
            limpiarTimer();
            timer = setTimeout(() => {
                copied.value = false;
                timer = null;
            }, feedbackMs);

            return true;
        } catch (error) {
            return fallo();
        }
    }

    /**
     * Estado de copia fallida: no se marca `copied` y, si se pidió, se avisa.
     * @returns {false}
     */
    function fallo() {
        copied.value = false;
        if (errorMessage) toast.error(errorMessage);
        return false;
    }

    /**
     * Copia con un `<textarea>` temporal (navegadores sin Clipboard API).
     *
     * Devuelve lo que devuelve `document.execCommand('copy')`: false significa
     * que NO se copió. El textarea se retira siempre, incluso si execCommand
     * lanza, para no dejar basura en el DOM.
     *
     * @param {string} value
     * @returns {boolean}
     */
    function copiarConTextarea(value) {
        const area = document.createElement('textarea');
        area.value = value;
        area.setAttribute('readonly', '');
        area.style.position = 'absolute';
        area.style.left = '-9999px';
        document.body.appendChild(area);

        try {
            area.select();
            return document.execCommand('copy');
        } finally {
            document.body.removeChild(area);
        }
    }

    onUnmounted(limpiarTimer);

    return { copied, copy };
}
